import React, { useEffect } from 'react';

import { Typography, Container } from '@hpstellar/core/visId';
import PriceAtc from '../price-atc/price-atc';

import { useMSRPProps } from '../../../hooks/useProductPrice';
import withPageComponents from '../../../page/components/with-page-components';
import ProductJsonLd from '../../components/sticky-header/cto-schema-markup';
import { Helpers } from '../../../core/src/helpers';
import Loader from '../../components/sticky-header/loader';
import DeliveryMessage from '../../components/sticky-header/delivery-message';
import GameFPS from '../../components/game-fps';
import useAddCTOToCart from '../../components/sticky-header/useAddCTOToCart';

import '../../components/sticky-header/sticky-header.less';
import './sticky-header.less';

const EMPTY_OBJ = {};

const StickyHeader = ({ baseConfig, device, froggerMessage, trackCustomMetric, innerWidth, ctoSettings }) => {
    const { shipsText, title, ULList, sku } = baseConfig || EMPTY_OBJ;
    const msrp = useMSRPProps({ position: 'bottom-start' });
    const isMobile = device === 'mobile';
    const shippingMessage = froggerMessage || shipsText;
    const { disableFPS } = ctoSettings || {};
    const displayFPSCalc = !disableFPS && Object.keys(ULList || {}).length > 0 && !isMobile;
    const { buttonProps, addingToCart, price } = useAddCTOToCart(trackCustomMetric, { endIcon: false });

    const jsonLD = <ProductJsonLd price={price} />;

    const small = innerWidth < 768;

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;

        const sentinel = document.querySelector('.sticky-sentinel');
        const stickyItem = document.querySelector('.configure-header-container');

        if (!sentinel || !stickyItem) return undefined;

        let isPinned = false;
        let lastToggle = 0;
        const MIN_TOGGLE_INTERVAL = 150;

        const observer = new IntersectionObserver(
            entries => {
                const entry = entries[0];

                if (!entry) return;

                const nextPinned = entry.isIntersecting === false;

                const now = performance.now();
                if (nextPinned !== isPinned && now - lastToggle < MIN_TOGGLE_INTERVAL) return;

                if (nextPinned === isPinned) return;

                isPinned = nextPinned;
                lastToggle = now;
                stickyItem.classList.toggle('is-pinned', nextPinned);
            },
            {
                threshold: [0],
                rootMargin: '-10px 0px 0px 0px',
            },
        );

        observer.observe(sentinel);

        return () => observer.disconnect();
    }, []);

    return (
        <>
            <div className="sticky-sentinel" />
            <div
                className={`configure-header-container${isMobile ? ' mobile' : ''}${displayFPSCalc ? ' fps-calc' : ''}`}
            >
                <Loader addingToCart={addingToCart} />
                <div className="configure-header">
                    <Container className="sticky-header-wrapper">
                        {jsonLD}

                        <div className="header-content">
                            <div className="header-content-title-container">
                                <Typography
                                    className="header-content-title"
                                    tag="h4"
                                    as="h4"
                                    variant={isMobile ? 'headingS' : 'headingM'}
                                >
                                    {title}
                                </Typography>
                                <Typography className="header-sku" tag="p" variant="footnotes">
                                    {sku}
                                </Typography>
                                {shippingMessage && (
                                    <Typography
                                        variant="bodyS"
                                        className="header shipping-html-visId"
                                        dangerouslySetInnerHTML={Helpers.createMarkup(shippingMessage)}
                                    />
                                )}
                                <DeliveryMessage visId={true} />
                            </div>
                            {displayFPSCalc && <GameFPS />}
                            <PriceAtc
                                className="configure-header-price-atc"
                                price={price}
                                buttonProps={buttonProps}
                                msrp={msrp}
                                slim={small}
                                alignment={small ? 'left' : 'right'}
                                showLabel={small}
                                stacked={displayFPSCalc}
                                isMobile={isMobile}
                            />
                        </div>
                    </Container>
                </div>
            </div>
        </>
    );
};
StickyHeader.whyDidYouRender = true;

export default withPageComponents(StickyHeader, {
    components: ['baseConfig', 'shipDate', 'ctoProductInitial', 'froggerMessage', 'ctoSettings'],
    device: true,
});
